import { LineArrowLeft } from '@qr-front/icons/line-arrow-left'
import { LineClose } from '@qr-front/icons/line-close'
import { buttonCls } from '@qr-front/ui/components/Button'
import { container } from '@qr-front/ui/patterns'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { useAtomValue } from 'jotai'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { BUILDER_EDIT_MODES, ROUTES } from '@/constants'
import { Link, usePathname } from '@/i18n-routing'

import { captureBuilderEvent, STEP_TO_INDEX } from '../../builderAnalytics'
import { hasLandedFromAltLandingAtom, qrTypeAtom } from '../../generatorState'
import { GENERATOR_SEARCH_PARAM, QR_TYPE_TO_PARAM } from '../../generatorTypes'
import { useCurrGeneratorStep } from '../../hooks'
import { CreateModeGeneratorStepper } from '../GeneratorSteppers'
import { ButtonBackDesktop } from './ButtonBackDesktop'
import { NextButton } from './NextButton'
import { PreviewButton } from './PreviewButton'

const MobileView = ({
	backUrl,
	isChangeTypeMode,
}: {
	backUrl: string
	isChangeTypeMode: boolean
}) => {
	const currStep = useCurrGeneratorStep()

	const handleBackClick = () => {
		captureBuilderEvent('builder_step_back', {
			step_index: STEP_TO_INDEX[currStep],
			section: 'wizard_stepper',
		})
	}

	const closeButton = (
		<Link
			href={ROUTES.cabinet.qrCodes}
			className={cn(
				buttonCls({ visual: 'soft', size: 'icon-large' }),
				'text-grey hover:text-blue',
			)}
		>
			<LineClose className="size-4" />
		</Link>
	)

	return (
		<div
			className={cn(
				container,
				'flex h-20 items-center justify-between gap-4 desktop:hidden',
			)}
		>
			<div className="flex shrink-0 gap-4">
				{isChangeTypeMode && closeButton}
				<Link
					href={backUrl}
					onClick={handleBackClick}
					className={cn(
						buttonCls({ visual: 'soft', size: 'icon-large' }),
						'text-grey hover:text-blue',
					)}
				>
					<LineArrowLeft className="size-4" />
				</Link>
				<PreviewButton />
			</div>
			<NextButton
				editMode={false}
				isChangeTypeMode={isChangeTypeMode}
				className="min-w-0 shrink grow desktop:shrink-0 tablet:grow-0"
			/>
		</div>
	)
}

const DesktopView = ({
	backUrl,
	isChangeTypeMode,
}: {
	backUrl: string
	isChangeTypeMode: boolean
}) => {
	const t = useTranslations()
	const pathname = usePathname()

	const showStepper = getVersionForClone({ base: true, clone1: false })
	const pathnameMatchesTypeStep = pathname === ROUTES.generator.typeStep

	if (!showStepper && pathnameMatchesTypeStep) return null

	return (
		<div
			className={cn(
				container,
				'hidden h-20 items-center gap-x-8 desktop:grid desktop:grid-cols-[1fr_minmax(280px,max-content)]',
			)}
		>
			<div className="flex min-w-0 items-center justify-between">
				{isChangeTypeMode && (
					<Link
						href={ROUTES.cabinet.qrCodes}
						className={cn(
							buttonCls({ visual: 'soft', size: 'large' }),
							'type-body-medium px-4 font-normal',
						)}
					>
						{t('generator__footer__edit_mode__exit_button')}
					</Link>
				)}
				<ButtonBackDesktop url={backUrl} />
				{showStepper && (
					<CreateModeGeneratorStepper
						className="only:ml-auto"
						isChangeTypeMode={isChangeTypeMode}
					/>
				)}
			</div>
			<div className="flex justify-center">
				<NextButton
					className="grow mx-7"
					editMode={false}
					isChangeTypeMode={isChangeTypeMode}
				/>
			</div>
		</div>
	)
}

export const CreateModeGeneratorFooter = ({
	isChangeTypeMode,
}: {
	isChangeTypeMode: boolean
}) => {
	const searchParams = useSearchParams()
	const target = searchParams.get(GENERATOR_SEARCH_PARAM.Target)

	const currStep = useCurrGeneratorStep()
	const qrType = useAtomValue(qrTypeAtom)

	const hasLandedFromAltLanding = useAtomValue(hasLandedFromAltLandingAtom)

	const isDesignStep = currStep === 'design'

	const typeStepBase = !hasLandedFromAltLanding
		? ROUTES.generator.typeStep
		: ROUTES.altLanding

	const typeStepWithParams = `${ROUTES.generator.typeStep}/${QR_TYPE_TO_PARAM[qrType!]}?${searchParams}`

	const changeTypeQuery = isChangeTypeMode
		? `?${GENERATOR_SEARCH_PARAM.Target}=${target}&${GENERATOR_SEARCH_PARAM.Edit}=${BUILDER_EDIT_MODES.TYPE}`
		: ''

	const backUrl = isDesignStep
		? typeStepWithParams
		: `${typeStepBase}${changeTypeQuery}`

	return (
		<>
			<MobileView backUrl={backUrl} isChangeTypeMode={isChangeTypeMode} />
			<DesktopView
				backUrl={backUrl}
				isChangeTypeMode={isChangeTypeMode}
			/>
		</>
	)
}
